// src/components/layout/Navbar.jsx

import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { HiMenuAlt3, HiX } from "react-icons/hi";

import { FaHome, FaInfoCircle, FaLeaf, FaStar } from "react-icons/fa";

import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

import { HashLink } from "react-router-hash-link";

import gsap from "gsap";

import LogoSemFundo from "../../assets/LogosemFundo.png";

/* ==========================================================================
   CONFIGURAÇÕES
   ========================================================================== */

const DEFAULT_PHONE = "5511921881464";

const NAV_LINKS = [
  {
    id: "inicio",
    name: "Início",
    to: "/#home",
    type: "hash",
    icon: FaHome,
  },

  {
    id: "sobre",
    name: "Sobre",
    to: "/#sobre",
    type: "hash",
    icon: FaInfoCircle,
  },

  {
    id: "tratamentos",
    name: "Tratamentos",
    to: "/servicos",
    type: "route",
    icon: FaLeaf,
  },

  {
    id: "link-bio",
    name: "Link Bio",
    to: "/LinkBio",
    type: "route",
    icon: FaStar,
  },
];
/* ==========================================================================
   UTILITÁRIOS
   ========================================================================== */

function isNormalNavigationClick(event) {
  return (
    event.button === 0 &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

function userPrefersReducedMotion() {
  if (typeof window === "undefined") {
    return true;
  }

  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/* ==========================================================================
   COMPONENTE PRINCIPAL
   ========================================================================== */

function Navbar({ phone = DEFAULT_PHONE }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [isScrolled, setIsScrolled] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return window.scrollY > 50;
  });

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navRef = useRef(null);
  const logoRef = useRef(null);

  const desktopLinksRef = useRef(null);
  const desktopCtaRef = useRef(null);

  const mobileToggleRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileLinksRef = useRef(null);
  const mobileCtaRef = useRef(null);

  const mobileTimelineRef = useRef(null);
  const menuInitializedRef = useRef(false);

  /* ==========================================================================
     TELEFONE E WHATSAPP
     ========================================================================== */

  const normalizedPhone = useMemo(() => {
    const receivedPhone = String(phone ?? "").replace(/\D/g, "");

    return receivedPhone || DEFAULT_PHONE;
  }, [phone]);

  const whatsappUrl = useMemo(() => {
    const message =
      "Olá! Vim pelo site da Clínica de Estética Catarino e gostaria de mais informações.";

    return `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(
      message,
    )}`;
  }, [normalizedPhone]);

  /* ==========================================================================
     CONTROLE DO MENU MOBILE
     ========================================================================== */

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((currentState) => !currentState);
  }, []);

  /* ==========================================================================
     ROLAGEM
     ========================================================================== */

  const scrollToPageTop = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: userPrefersReducedMotion() ? "auto" : "smooth",
    });
  }, []);

  const scrollToTopAfterNavigation = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    /*
     * O primeiro frame aguarda a alteração da rota.
     * O segundo aguarda a montagem da nova página.
     */
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scrollToPageTop();
      });
    });
  }, [scrollToPageTop]);

  const handleRouteNavigation = useCallback(
    (event, destination) => {
      closeMobileMenu();

      if (!isNormalNavigationClick(event)) {
        return;
      }

      event.preventDefault();

      navigate(destination);

      scrollToTopAfterNavigation();
    },
    [closeMobileMenu, navigate, scrollToTopAfterNavigation],
  );

  const handleLogoNavigation = useCallback(
    (event) => {
      handleRouteNavigation(event, "/");
    },
    [handleRouteNavigation],
  );

  const scrollToSection = useCallback((element) => {
    if (!element || typeof window === "undefined") {
      return;
    }

    const navbarHeight = navRef.current?.offsetHeight ?? 0;

    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: elementPosition - navbarHeight - 16,
      left: 0,
      behavior: userPrefersReducedMotion() ? "auto" : "smooth",
    });
  }, []);

  /*
   * O link Início sempre vai exatamente para o topo,
   * mesmo que o elemento #home tenha margens.
   */
  const scrollHomeToTop = useCallback(() => {
    scrollToPageTop();
  }, [scrollToPageTop]);

  /* ==========================================================================
     CLASSES DOS HASH LINKS
     ========================================================================== */

  const getHashLinkClassName = useCallback(
    (hash, mobile = false) => {
      const currentHash = location.hash.toLowerCase();

      const targetHash = hash.toLowerCase();

      const isHomeRoute = location.pathname === "/";

      const isHomeHash =
        targetHash === "#home" &&
        (currentHash === "" || currentHash === "#home");

      const isActive =
        isHomeRoute && (isHomeHash || currentHash === targetHash);

      const baseClass = mobile ? "mobile-menu__link" : "navbar__link";

      const activeClass = mobile
        ? "mobile-menu__link--active"
        : "navbar__link--active";

      return [baseClass, isActive ? activeClass : ""].filter(Boolean).join(" ");
    },
    [location.pathname, location.hash],
  );

  /* ==========================================================================
     ESTADO DA NAVBAR AO ROLAR
     ========================================================================== */

  useEffect(() => {
    const handleScroll = () => {
      const nextState = window.scrollY > 50;

      setIsScrolled((currentState) => {
        if (currentState === nextState) {
          return currentState;
        }

        return nextState;
      });
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /* ==========================================================================
     BLOQUEIO DO BODY
     ========================================================================== */

  useEffect(() => {
    document.body.classList.toggle("no-scroll", isMobileMenuOpen);

    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [isMobileMenuOpen]);

  /* ==========================================================================
     FECHAR COM ESCAPE
     ========================================================================== */

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key !== "Escape" || !isMobileMenuOpen) {
        return;
      }

      closeMobileMenu();

      window.requestAnimationFrame(() => {
        mobileToggleRef.current?.focus();
      });
    };

    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isMobileMenuOpen, closeMobileMenu]);

  /* ==========================================================================
     FECHAR AO CLICAR FORA
     ========================================================================== */

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!isMobileMenuOpen) {
        return;
      }

      const navbar = navRef.current;

      if (navbar && !navbar.contains(event.target)) {
        closeMobileMenu();
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);

    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick);
    };
  }, [isMobileMenuOpen, closeMobileMenu]);

  /* ==========================================================================
     FECHAR AO VOLTAR PARA DESKTOP
     ========================================================================== */

  useEffect(() => {
    const desktopMediaQuery = window.matchMedia("(min-width: 992px)");

    const handleViewportChange = (event) => {
      if (event.matches) {
        closeMobileMenu();
      }
    };

    desktopMediaQuery.addEventListener("change", handleViewportChange);

    return () => {
      desktopMediaQuery.removeEventListener("change", handleViewportChange);
    };
  }, [closeMobileMenu]);

  /* ==========================================================================
     FECHAR AO TROCAR DE ROTA
     ========================================================================== */

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, location.hash, closeMobileMenu]);

  /* ==========================================================================
     ANIMAÇÃO DO MENU MOBILE
     ========================================================================== */

  useLayoutEffect(() => {
    const menu = mobileMenuRef.current;

    const linksContainer = mobileLinksRef.current;

    const cta = mobileCtaRef.current;
    const toggle = mobileToggleRef.current;

    if (!menu) {
      return undefined;
    }

    mobileTimelineRef.current?.kill();

    const links = linksContainer?.querySelectorAll(".mobile-menu__link") ?? [];

    const reduceMotion = userPrefersReducedMotion();

    if (!menuInitializedRef.current) {
      menuInitializedRef.current = true;

      gsap.set(menu, {
        display: "none",
        height: 0,
        autoAlpha: 0,
        y: -14,
        pointerEvents: "none",
      });

      gsap.set(links, {
        autoAlpha: 0,
        x: -18,
      });

      if (cta) {
        gsap.set(cta, {
          autoAlpha: 0,
          y: 14,
        });
      }

      if (!isMobileMenuOpen) {
        return undefined;
      }
    }

    if (reduceMotion) {
      gsap.set(menu, {
        display: isMobileMenuOpen ? "block" : "none",

        height: isMobileMenuOpen ? "auto" : 0,

        autoAlpha: isMobileMenuOpen ? 1 : 0,

        y: 0,

        pointerEvents: isMobileMenuOpen ? "auto" : "none",
      });

      gsap.set(links, {
        autoAlpha: isMobileMenuOpen ? 1 : 0,

        x: 0,
      });

      if (cta) {
        gsap.set(cta, {
          autoAlpha: isMobileMenuOpen ? 1 : 0,

          y: 0,
        });
      }

      return undefined;
    }

    const timeline = gsap.timeline({
      defaults: {
        ease: "power3.out",
      },
    });

    mobileTimelineRef.current = timeline;

    if (isMobileMenuOpen) {
      gsap.set(menu, {
        display: "block",
        pointerEvents: "auto",
      });

      timeline.fromTo(
        menu,
        {
          height: 0,
          autoAlpha: 0,
          y: -14,
        },
        {
          height: "auto",
          autoAlpha: 1,
          y: 0,
          duration: 0.52,
          ease: "power3.out",
        },
      );

      if (links.length > 0) {
        timeline.fromTo(
          links,
          {
            autoAlpha: 0,
            x: -22,
          },
          {
            autoAlpha: 1,
            x: 0,
            duration: 0.42,
            stagger: 0.08,
            clearProps: "transform",
          },
          "-=0.3",
        );
      }

      if (cta) {
        timeline.fromTo(
          cta,
          {
            autoAlpha: 0,
            y: 18,
          },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.42,
            clearProps: "transform",
          },
          "-=0.22",
        );
      }

      if (toggle) {
        timeline.fromTo(
          toggle,
          {
            scale: 0.92,
            rotate: -16,
          },
          {
            scale: 1,
            rotate: 0,
            duration: 0.36,
            clearProps: "transform",
          },
          0,
        );
      }
    } else {
      if (cta) {
        timeline.to(cta, {
          autoAlpha: 0,
          y: 12,
          duration: 0.22,
          ease: "power2.in",
        });
      }

      if (links.length > 0) {
        timeline.to(
          links,
          {
            autoAlpha: 0,
            x: -14,
            duration: 0.24,

            stagger: {
              each: 0.035,
              from: "end",
            },

            ease: "power2.in",
          },
          cta ? "<" : 0,
        );
      }

      timeline.to(
        menu,
        {
          height: 0,
          autoAlpha: 0,
          y: -12,
          duration: 0.36,
          ease: "power3.inOut",

          onComplete: () => {
            gsap.set(menu, {
              display: "none",
              pointerEvents: "none",
            });
          },
        },
        "-=0.06",
      );
    }

    return () => {
      timeline.kill();
    };
  }, [isMobileMenuOpen]);

  /* ==========================================================================
     ENTRADA INICIAL DA NAVBAR
     ========================================================================== */

  useLayoutEffect(() => {
    const navbar = navRef.current;

    if (!navbar) {
      return undefined;
    }

    if (userPrefersReducedMotion()) {
      gsap.set(
        [
          navbar,
          logoRef.current,
          desktopCtaRef.current,
          mobileToggleRef.current,
        ],
        {
          clearProps: "transform,opacity,visibility",
        },
      );

      return undefined;
    }

    const desktopLinks =
      desktopLinksRef.current?.querySelectorAll(".navbar__link") ?? [];

    const context = gsap.context(() => {
      const timeline = gsap.timeline({
        defaults: {
          ease: "power3.out",
        },
      });

      timeline.fromTo(
        navbar,
        {
          y: -90,
          autoAlpha: 0,
        },
        {
          y: 0,
          autoAlpha: 1,
          duration: 0.85,

          clearProps: "transform,opacity,visibility",
        },
      );

      if (logoRef.current) {
        timeline.fromTo(
          logoRef.current,
          {
            x: -24,
            autoAlpha: 0,
          },
          {
            x: 0,
            autoAlpha: 1,
            duration: 0.62,

            clearProps: "transform,opacity,visibility",
          },
          "-=0.48",
        );
      }

      if (desktopLinks.length > 0) {
        timeline.fromTo(
          desktopLinks,
          {
            y: -12,
            autoAlpha: 0,
          },
          {
            y: 0,
            autoAlpha: 1,
            duration: 0.48,
            stagger: 0.08,

            clearProps: "transform,opacity,visibility",
          },
          "-=0.38",
        );
      }

      if (desktopCtaRef.current) {
        timeline.fromTo(
          desktopCtaRef.current,
          {
            x: 20,
            autoAlpha: 0,
          },
          {
            x: 0,
            autoAlpha: 1,
            duration: 0.55,

            clearProps: "transform,opacity,visibility",
          },
          "-=0.35",
        );
      }

      if (mobileToggleRef.current) {
        timeline.fromTo(
          mobileToggleRef.current,
          {
            scale: 0.82,
            autoAlpha: 0,
          },
          {
            scale: 1,
            autoAlpha: 1,
            duration: 0.42,

            clearProps: "transform,opacity,visibility",
          },
          "-=0.32",
        );
      }

      timeline.eventCallback("onComplete", () => {
        if (desktopCtaRef.current) {
          gsap.set(desktopCtaRef.current, {
            clearProps: "transform,opacity,visibility",
          });
        }
      });
    }, navbar);

    return () => {
      context.revert();
    };
  }, []);

  /* ==========================================================================
     SOMBRA AO ROLAR
     ========================================================================== */

  useEffect(() => {
    const navbar = navRef.current;

    if (!navbar) {
      return undefined;
    }

    gsap.to(navbar, {
      boxShadow: isScrolled
        ? "0 14px 38px rgba(45, 35, 29, 0.10)"
        : "0 0 0 rgba(45, 35, 29, 0)",

      duration: 0.45,
      ease: "power2.out",
      overwrite: "auto",
    });

    return undefined;
  }, [isScrolled]);

  /* ==========================================================================
     MICROINTERAÇÃO DA LOGO
     ========================================================================== */

  const handleLogoMouseEnter = useCallback(() => {
    const logo = logoRef.current;

    if (!logo || userPrefersReducedMotion()) {
      return;
    }

    gsap.to(logo, {
      y: -3,
      scale: 1.025,
      duration: 0.4,
      ease: "power2.out",
      overwrite: "auto",
    });
  }, []);

  const handleLogoMouseLeave = useCallback(() => {
    const logo = logoRef.current;

    if (!logo || userPrefersReducedMotion()) {
      return;
    }

    gsap.to(logo, {
      y: 0,
      scale: 1,
      duration: 0.42,
      ease: "power2.out",
      overwrite: "auto",
      clearProps: "transform",
    });
  }, []);

  /* ==========================================================================
     LINKS DESKTOP
     ========================================================================== */

  const renderDesktopLink = (link) => {
    const Icon = link.icon;

    if (link.type === "hash") {
      const hash = `#${link.to.split("#")[1] ?? ""}`;

      return (
        <HashLink
          smooth
          to={link.to}
          className={getHashLinkClassName(hash)}
          onClick={closeMobileMenu}
          scroll={link.id === "inicio" ? scrollHomeToTop : scrollToSection}
        >
          <Icon className="nav-icon" aria-hidden="true" />

          <span>{link.name}</span>
        </HashLink>
      );
    }

    return (
      <NavLink
        to={link.to}
        onClick={(event) => handleRouteNavigation(event, link.to)}
        className={({ isActive }) =>
          ["navbar__link", isActive ? "navbar__link--active" : ""]
            .filter(Boolean)
            .join(" ")
        }
      >
        <Icon className="nav-icon" aria-hidden="true" />

        <span>{link.name}</span>
      </NavLink>
    );
  };

  /* ==========================================================================
     LINKS MOBILE
     ========================================================================== */

  const renderMobileLink = (link) => {
    const Icon = link.icon;

    if (link.type === "hash") {
      const hash = `#${link.to.split("#")[1] ?? ""}`;

      return (
        <HashLink
          key={`mobile-${link.id}`}
          smooth
          to={link.to}
          role="menuitem"
          className={getHashLinkClassName(hash, true)}
          onClick={closeMobileMenu}
          scroll={link.id === "inicio" ? scrollHomeToTop : scrollToSection}
        >
          <Icon className="nav-icon" aria-hidden="true" />

          <span>{link.name}</span>
        </HashLink>
      );
    }

    return (
      <NavLink
        key={`mobile-${link.id}`}
        to={link.to}
        role="menuitem"
        onClick={(event) => handleRouteNavigation(event, link.to)}
        className={({ isActive }) =>
          ["mobile-menu__link", isActive ? "mobile-menu__link--active" : ""]
            .filter(Boolean)
            .join(" ")
        }
      >
        <Icon className="nav-icon" aria-hidden="true" />

        <span>{link.name}</span>
      </NavLink>
    );
  };

  /* ==========================================================================
     JSX
     ========================================================================== */

  return (
    <nav
      ref={navRef}
      className={[
        "navbar",
        isScrolled ? "navbar--scrolled" : "",
        isMobileMenuOpen ? "navbar--menu-open" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label="Navegação principal"
    >
      <div className="navbar__container">
        {/* Logo */}

        <Link
          ref={logoRef}
          to="/"
          className="navbar__logo"
          aria-label="Ir para a página inicial da Clínica de Estética Catarino"
          onClick={handleLogoNavigation}
          onMouseEnter={handleLogoMouseEnter}
          onMouseLeave={handleLogoMouseLeave}
        >
          <img
            src={LogoSemFundo}
            alt="Clínica de Estética Catarino"
            className="navbar__logo-image"
            width="190"
            height="72"
            loading="eager"
            decoding="async"
            draggable="false"
          />
        </Link>

        {/* Menu desktop */}

        <ul ref={desktopLinksRef} className="navbar__links">
          {NAV_LINKS.map((link) => (
            <li key={link.id}>{renderDesktopLink(link)}</li>
          ))}
        </ul>

        {/* CTA desktop */}

        <div ref={desktopCtaRef} className="navbar__cta">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="navbar__btn-cta"
            aria-label="Falar com a Dra. Fabiana pelo WhatsApp"
          >
            Falar com a Dra. Fabiana
          </a>
        </div>

        {/* Botão mobile */}

        <button
          ref={mobileToggleRef}
          type="button"
          className={[
            "navbar__mobile-toggle",
            isMobileMenuOpen ? "is-open" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={toggleMobileMenu}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-menu"
          aria-label={
            isMobileMenuOpen
              ? "Fechar menu de navegação"
              : "Abrir menu de navegação"
          }
        >
          {isMobileMenuOpen ? (
            <HiX size={30} aria-hidden="true" />
          ) : (
            <HiMenuAlt3 size={30} aria-hidden="true" />
          )}
        </button>
      </div>

      {/* Menu mobile */}

      <div
        ref={mobileMenuRef}
        id="mobile-menu"
        className={["navbar__mobile-menu", isMobileMenuOpen ? "is-open" : ""]
          .filter(Boolean)
          .join(" ")}
        aria-hidden={!isMobileMenuOpen}
      >
        <div
          ref={mobileLinksRef}
          className="mobile-menu__links"
          role="menu"
          aria-label="Menu mobile"
        >
          {NAV_LINKS.map(renderMobileLink)}
        </div>

        <div ref={mobileCtaRef} className="mobile-menu__cta">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--primary w-full"
            onClick={closeMobileMenu}
          >
            Chamar no WhatsApp
          </a>
        </div>
      </div>
    </nav>
  );
}

export default memo(Navbar);
